import { InsightApiResponse, InsightsResponse } from 'api/api.types';
import { getInsight, getSimilarIdeas } from 'api/Insight';
import { GetServerSideProps, NextPage } from 'next';
import React, { useEffect } from 'react';
import { Analytics, Events } from 'services/analytics';
import IdeaPage from 'src/page-components/source/IdeaPage';
import { slugify } from 'utils/global';
import { getIdeaPageMetadata } from 'utils/metadata.utils';
import { normalizeIdea } from 'utils/normalizers/source';

interface OwnProps {
  insight: InsightApiResponse;
  similarIdeasApiResponse?: InsightsResponse;
}

const IdeaDynamicPage: NextPage<OwnProps> = ({
  insight,
  similarIdeasApiResponse,
}) => {
  // Page accessed NSI-only

  useEffect(() => {
    Analytics.logEvent({
      eventName: Events.consumption.viewIdea,
      properties: {
        ideaId: insight.id,
        title: insight.title,
      },
      platforms: ['amplitude'],
    });
  }, [insight.id]);

  const normalizedIdea = normalizeIdea(insight);
  const { source } = normalizedIdea;

  const ideas = [
    normalizedIdea,
    ...(source?.ideas ?? []).filter(
      ideaFromMap => ideaFromMap.id !== normalizedIdea.id,
    ),
  ];

  if (!source) {
    // This should never happen
    return null;
  }

  return (
    <IdeaPage
      source={{ ...source, ideas }}
      similarIdeas={similarIdeasApiResponse?.objects.map(similarIdea =>
        normalizeIdea(similarIdea),
      )}
      pageMetadata={getIdeaPageMetadata({ insight })}
    />
  );
};

export const getServerSideProps: GetServerSideProps = async ({
  params,
  req,
}) => {
  if (!params) return { notFound: true };

  const insightId = Number(params.id);
  const slug = params.slug;

  const insight = await getInsight(insightId).catch(_ => undefined);

  if (!insight || !insight.article) {
    return { notFound: true };
  }

  const { article, title, content } = insight;

  const insightSlug = slugify(title, content);

  // Removed for now
  // We will progresively migrate idea pages to source pages
  // We will do it in 10 batches, hence the modulo 10
  // if (
  //   (POPULAR_IDEA_IDS.indexOf(insight.id) !== -1 ||
  //     shouldRedirectIdeaPage(insight.id)) &&
  //   article
  // ) {
  //   return {
  //     redirect: {
  //       destination: `/article/${article.id}/${slugify(article.title)}`,
  //       statusCode: 301,
  //     },
  //   };
  // }

  if (insightSlug !== slug) {
    return {
      redirect: {
        destination: `/idea/${insightId}/${insightSlug}`,
        // We want to have 301(permanent redirect), but keep this here in case we want to move back to 307
        // permanent: false,
        statusCode: 301,
      },
    };
  }

  const similarIdeasApiResponse: InsightsResponse | undefined | void =
    await getSimilarIdeas(insightId).catch(e => console.log(e));

  // This page should only be accessible if NSI, else we redirect to the corresponding source page.
  const token = req.cookies.token;

  if (token && article) {
    return {
      redirect: {
        destination: `/article/${article.id}/${slugify(
          article?.title,
        )}#idea_${insightId}`,
        statusCode: 307,
      },
    };
  }

  if (similarIdeasApiResponse) {
    return {
      props: {
        insight,
        similarIdeasApiResponse,
      },
    };
  } else {
    return {
      props: {
        insight,
      },
    };
  }
};

export default IdeaDynamicPage;
